import { Fragment, useEffect, useLayoutEffect, useRef, useState } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Lenis from "lenis";
import "./style.css";
import { AnimatedHeading } from "../../shared/TextFx";
import blogImage1 from "./../../assets/home-blog-img1.jpg";
import blogImage2 from "./../../assets/home-blog-img2.jpg";
import blogImage3 from "./../../assets/home-blog-img3.jpg";

gsap.registerPlugin(ScrollTrigger);

/* ---- Content ---- */
const TITLE = "How Esquire Works";
const SUBTITLE = "How It Moves";
const SUB_HEADING = "From request to delivery, one coordinated movement.";
const DESCRIPTION =
  "One 24/7 Control Tower. Every stage managed. Every handoff monitored.";

/* Images repeat in order: 1, 2, 3, 1, 2 ... */
const IMAGES = [blogImage1, blogImage2, blogImage3];

const STEPS_DATA = [
  {
    title: "Request",
    text: "Tell us where it is, where it needs to go and when it needs to arrive.",
    alt: "Shipment request being received by the Control Tower",
  },
  {
    title: "Route",
    text: "We determine the fastest viable movement to meet the required delivery time.",
    alt: "Planning the fastest route for a time critical shipment",
  },
  {
    title: "Move",
    text: "Pickup, carrier coordination, terminal handling, customs and connections are actively managed.",
    alt: "Shipment moving through carrier and terminal handling",
  },
  {
    title: "Deliver",
    text: "We stay accountable through to final delivery.",
    alt: "Shipment arriving for final delivery",
  },
].map((step, i) => ({ ...step, image: IMAGES[i % IMAGES.length] }));

const TOTAL = STEPS_DATA.length;
const STEPS = TOTAL - 1;

/* power3.inOut as a plain function for Lenis.scrollTo */
const power3InOut = (t) =>
  t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

/* Splits text into word spans for the reveal */
function SplitWords({ text, as: Tag = "p", className = "" }) {
  return (
    <Tag className={className} aria-label={text}>
      {text.split(" ").map((word, i) => (
        <Fragment key={i}>
          <span className="word" aria-hidden="true">
            {word}
          </span>{" "}
        </Fragment>
      ))}
    </Tag>
  );
}

export default function HowWorks() {
  const rootRef = useRef(null);
  const stageRef = useRef(null);
  const lenisRef = useRef(null);
  const stRef = useRef(null);
  const activeRef = useRef(0);
  const reduceRef = useRef(false);
  const [active, setActive] = useState(0);

  /* ---------------- Lenis + ScrollTrigger ---------------- */
  useLayoutEffect(() => {
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
    });
    lenisRef.current = lenis;
    lenis.on("scroll", ScrollTrigger.update);

    const tick = (time) => lenis.raf(time * 1000);
    gsap.ticker.add(tick);
    gsap.ticker.lagSmoothing(0);

    const ctx = gsap.context(() => {
      const mm = gsap.matchMedia();

      mm.add(
        {
          desktop: "(min-width: 900px)",
          mobile: "(max-width: 899px)",
          reduce: "(prefers-reduced-motion: reduce)",
        },
        (c) => {
          const { desktop, reduce } = c.conditions;
          reduceRef.current = reduce;

          const stepX = () => window.innerWidth * (desktop ? 0.27 : 0.34);
          const stepY = () => window.innerHeight * (desktop ? 0.66 : 0.5);

          /* card state for its offset from the active slot */
          const state = (off) => {
            if (off === 0) {
              return {
                x: 0,
                y: 0,
                scale: 1,
                opacity: 1,
                filter: "grayscale(0%) blur(0px)",
                zIndex: 3,
              };
            }
            if (off > 0) {
              return {
                x: () => off * stepX(),
                y: () => off * stepY(),
                scale: 0.94,
                opacity: 0.3,
                filter: "grayscale(100%) blur(0px)",
                zIndex: 2,
              };
            }
            return {
              x: () => off * stepX() * 0.45,
              y: () => off * stepY() * 1.05,
              scale: 0.9,
              opacity: 0,
              filter: "grayscale(40%) blur(10px)",
              zIndex: 1,
            };
          };

          const cards = gsap.utils.toArray(".how-works__card");
          cards.forEach((card, i) => gsap.set(card, state(i)));

          const tl = gsap.timeline({
            defaults: { ease: "power2.inOut", duration: 1 },
            scrollTrigger: {
              trigger: stageRef.current,
              start: "top top",
              end: () => `+=${STEPS * window.innerHeight}`,
              pin: true,
              scrub: reduce ? true : 1,
              invalidateOnRefresh: true,
              snap: reduce
                ? false
                : {
                    snapTo: 1 / STEPS,
                    duration: { min: 0.3, max: 0.8 },
                    delay: 0.05,
                    ease: "power3.inOut",
                  },
              onUpdate: (self) => {
                const idx = Math.round(self.progress * STEPS);
                if (idx !== activeRef.current) {
                  activeRef.current = idx;
                  setActive(idx);
                }
              },
            },
          });

          for (let s = 1; s <= STEPS; s++) {
            cards.forEach((card, i) => tl.to(card, state(i - s), s - 1));
          }

          stRef.current = tl.scrollTrigger;
        }
      );
    }, rootRef);

    return () => {
      ctx.revert();
      gsap.ticker.remove(tick);
      lenis.destroy();
    };
  }, []);

  /* ---------------- Counter + step text reveal ---------------- */
  useEffect(() => {
    const root = rootRef.current;
    const reduce = reduceRef.current;
    const strip = root.querySelector(".how-works__counter-strip");
    const descs = root.querySelectorAll(".how-works__desc");

    gsap.to(strip, {
      yPercent: -10 * (active + 1),
      duration: reduce ? 0 : 0.9,
      ease: "power4.inOut",
    });

    descs.forEach((desc, i) => {
      const words = desc.querySelectorAll(".word");
      gsap.killTweensOf(words);

      if (i === active) {
        gsap.set(desc, { autoAlpha: 1 });
        gsap.fromTo(
          words,
          { opacity: 0, yPercent: 40 },
          {
            opacity: 1,
            yPercent: 0,
            duration: reduce ? 0 : 0.7,
            ease: "power3.out",
            stagger: reduce ? 0 : 0.025,
            delay: reduce ? 0 : 0.15,
          }
        );
      } else {
        gsap.to(words, {
          opacity: 0,
          duration: reduce ? 0 : 0.35,
          ease: "power2.in",
          stagger: reduce ? 0 : 0.008,
          onComplete: () => gsap.set(desc, { autoAlpha: 0 }),
        });
      }
    });
  }, [active]);

  /* ---------------- Nav ---------------- */
  const goTo = (i) => {
    const st = stRef.current;
    const lenis = lenisRef.current;
    if (!st || !lenis) return;
    const clamped = Math.max(0, Math.min(STEPS, i));
    lenis.scrollTo(st.start + (st.end - st.start) * (clamped / STEPS), {
      duration: reduceRef.current ? 0 : 1.4,
      easing: power3InOut,
    });
  };

  return (
    <section id="how-it-works" className="how-works" ref={rootRef}>
      {/* ---------- Header: heading left, copy right ---------- */}
      <div className="how-works__header">
        <div className="how-works__header-left">
          <AnimatedHeading className="h-display how-works__title" text={TITLE} />
        </div>

        <div className="how-works__header-right">
          <p className="how-works__subtitle">
            <span className="how-works__subtitle-text">{SUBTITLE}</span>
            <span className="how-works__subtitle-bar" aria-hidden="true" />
          </p>
          <h4 className="how-works__sub-heading">{SUB_HEADING}</h4>
          <p className="how-works__para-desc">{DESCRIPTION}</p>
        </div>
      </div>

      {/* ---------- Pinned stage ---------- */}
      <div className="how-works__stage" ref={stageRef}>
        <div className="how-works__cards">
          {STEPS_DATA.map((step) => (
            <figure className="how-works__card" key={step.title}>
              <img src={step.image} alt={step.alt} />
            </figure>
          ))}
        </div>

        <div className="how-works__descs">
          {STEPS_DATA.map((step) => (
            <div className="how-works__desc" key={step.title}>
              <SplitWords as="h4" className="how-works__step-title" text={step.title} />
              <SplitWords className="how-works__step-text" text={step.text} />
            </div>
          ))}
        </div>

        {/* Counter 01 – 04 */}
        <div className="how-works__counter" aria-hidden="true">
          <div className="how-works__counter-digit">0</div>
          <div className="how-works__counter-digit">
            <div className="how-works__counter-strip">
              {Array.from({ length: 10 }, (_, n) => (
                <span key={n}>{n}</span>
              ))}
            </div>
          </div>
        </div>

        {/* Nav (tablet + mobile) */}
        <nav className="how-works__nav" aria-label="How it works steps">
          <button
            type="button"
            className="how-works__nav-btn"
            aria-label="Previous step"
            disabled={active === 0}
            onClick={() => goTo(active - 1)}
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path d="M11.625 6H0.375" />
              <path d="M4.125 2.25L0.375 6L4.125 9.75" />
            </svg>
          </button>

          <ol className="how-works__dashes" aria-label="Step index">
            {STEPS_DATA.map((step, i) => (
              <li key={step.title}>
                <button
                  type="button"
                  className={`how-works__dash${active === i ? " how-works__dash--active" : ""}`}
                  aria-label={`Go to step ${i + 1}`}
                  aria-current={active === i}
                  onClick={() => goTo(i)}
                />
              </li>
            ))}
          </ol>

          <button
            type="button"
            className="how-works__nav-btn"
            aria-label="Next step"
            disabled={active === STEPS}
            onClick={() => goTo(active + 1)}
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path d="M0.375 6H11.625" />
              <path d="M7.875 2.25L11.625 6L7.875 9.75" />
            </svg>
          </button>
        </nav>
      </div>
    </section>
  );
}
